import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { IconButton, Stack } from '@mui/material'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import moment, { type Moment } from 'moment'
import React, { useState } from 'react'
import { i18n } from '../../domain/index/index'

export type DateUnit = 'day' | 'week' | 'month'

export type DateStepperProps = {
  date: string
  onDateChange: (date: string) => void
  dateUnit?: DateUnit
  dateFormat?: string
  className?: string
  showLeftArrow?: boolean
  showRightArrow?: boolean
  scrollToBottom?: boolean
}

const ISO_FORMAT = 'YYYY-MM-DD'

const buildFormatByUnit = (): Record<DateUnit, string> => ({
  day: 'DD.MM.YYYY',
  week: `[${i18n.t('common.week')}] WW / YYYY`,
  month: 'MM / YYYY',
})

const viewsByUnit: Record<DateUnit, Array<'year' | 'month' | 'day'>> = {
  day: ['year', 'month', 'day'],
  week: ['year', 'month', 'day'],
  month: ['year', 'month'],
}

const scrollToPosition = (position: number) => {
  setTimeout(() => {
    window.scrollTo({ left: 0, top: position, behavior: 'smooth' })
  }, 500)
}

const DateStepper: React.FC<DateStepperProps> = ({
  date,
  onDateChange,
  dateUnit = 'day',
  dateFormat = ISO_FORMAT,
  className,
  showLeftArrow = true,
  showRightArrow = true,
  scrollToBottom = false,
}) => {
  const [open, setOpen] = useState(false)
  const formatByUnit = buildFormatByUnit()

  const handleChange = (next: string) => {
    onDateChange(next)
    if (scrollToBottom) {
      scrollToPosition(window.scrollY)
    }
  }

  const shift = (amount: number) => {
    handleChange(moment(date).add(amount, dateUnit).format(dateFormat))
  }

  const handlePickerChange = (value: Moment | null) => {
    if (value && value.isValid()) {
      handleChange(value.format(dateFormat))
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Stack direction='row' alignItems='center' spacing={1} justifyContent='center' className={className}>
        <IconButton
          data-testid='date-bar-prev-date-button'
          aria-label={i18n.t('dateStepper.previous')}
          disabled={!showLeftArrow}
          onClick={() => shift(-1)}
        >
          <ArrowBackIcon />
        </IconButton>
        <DesktopDatePicker
          value={moment(date)}
          format={formatByUnit[dateUnit]}
          onChange={handlePickerChange}
          views={viewsByUnit[dateUnit]}
          label={i18n.t('dateField.defaultLabel')}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          slotProps={{
            textField: {
              variant: 'outlined',
              onClick: () => setOpen(true),
              inputProps: { style: { cursor: 'pointer' } },
            },
            popper: {
              placement: 'bottom',
              modifiers: [
                {
                  name: 'offset',
                  options: { offset: [0, 8] },
                },
              ],
            },
          }}
        />

        <IconButton
          data-testid='date-bar-next-date-button'
          aria-label={i18n.t('dateStepper.next')}
          disabled={!showRightArrow}
          onClick={() => shift(1)}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Stack>
    </LocalizationProvider>
  )
}

export default DateStepper
